import { ArrowForward } from "@mui/icons-material";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { format, startOfToday } from "date-fns";
import React, { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import Pagination from "../Global/Pagination";

const GOLD = "#ffc000";
const PRIMARY = "#000c66";

export const EventCard = () => {
  const [events, setEvents] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const postsPerPage = 5;
  const navigate = useNavigate();

  React.useEffect(() => {
    const getEvents = async () => {
      try {
        // Only upcoming events: anything dated today or later.
        const q = query(
          collection(db, "events"),
          where("eventdate", ">=", startOfToday()),
          orderBy("eventdate", "asc")
        );
        const snap = await getDocs(q);
        setEvents(
          snap.docs.map((d) => ({
            _id: d.id,
            ...d.data(),
            eventdate: d.data().eventdate?.toDate() || new Date(),
          }))
        );
      } catch (err) {
        console.error(err.message);
      }
    };
    getEvents();
  }, []);

  const lastIndex = currentPage * postsPerPage;
  const currentPosts = events.slice(lastIndex - postsPerPage, lastIndex);

  const go = (slug) => {
    navigate(`/events/view/${slug}`);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <Fragment>
      <div
        className="mx-auto px-4 md:px-8 lg:px-16 py-10"
        style={{ maxWidth: 900 }}
      >
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ border: `2px dashed ${GOLD}66` }}
            >
              <span style={{ color: `${GOLD}88`, fontSize: 24 }}>✦</span>
            </div>
            <p className="font-aref text-lg font-bold" style={{ color: PRIMARY, opacity: 0.4 }}>
              No upcoming events
            </p>
            <p className="font-abel text-sm" style={{ color: "#aaa" }}>
              Check back soon for upcoming programs.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {currentPosts.map((event) => (
              <div
                key={event._id}
                className="flex flex-col sm:flex-row"
                style={{
                  border: "1px solid #ececec",
                  borderLeft: `4px solid ${GOLD}`,
                  borderRadius: 10,
                  overflow: "hidden",
                  boxShadow: "0 2px 18px rgba(0,0,0,0.06)",
                  transition: "transform 0.22s ease, box-shadow 0.22s ease",
                  minHeight: 180,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 14px 36px rgba(0,12,102,0.10)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 18px rgba(0,0,0,0.06)";
                }}
                onClick={() => go(event.slug)}
              >
                {/* Image */}
                <div
                  className="shrink-0 w-full sm:w-56 md:w-64 lg:w-72"
                  style={{ position: "relative", overflow: "hidden", minHeight: 180 }}
                >
                  {event.imgSrc ? (
                    <img
                      src={event.imgSrc}
                      alt={event.title}
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        display: "block",
                        transition: "transform 0.4s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                    />
                  ) : (
                    <div style={{
                      position: "absolute", inset: 0, background: "#f3f3f3",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <span style={{ color: `${GOLD}66`, fontSize: 40 }}>✦</span>
                    </div>
                  )}

                  {/* Date badge */}
                  <div style={{
                    position: "absolute", top: 12, left: 12,
                    background: GOLD, borderRadius: 4,
                    padding: "3px 10px", display: "flex", alignItems: "center", gap: 5,
                  }}>
                    <svg viewBox="0 0 16 16" style={{ width: 11, height: 11, fill: PRIMARY, flexShrink: 0 }}>
                      <path d="M5 1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v1h16V4a2 2 0 0 0-2-2h-1V1a1 1 0 0 0-2 0v1H5V1zm-3 6v6a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H2z" />
                    </svg>
                    <span className="font-abel font-bold text-xs" style={{ color: PRIMARY }}>
                      {format(new Date(event.eventdate), "do MMM, yyyy")}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div
                  className="flex flex-col justify-center gap-3"
                  style={{ padding: "20px 24px", flex: 1 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span
                      className="font-abel text-xs uppercase tracking-widest"
                      style={{ color: "#bbb" }}
                    >
                      {format(new Date(event.eventdate), "eee · h:mma")}
                    </span>
                  </div>

                  <h3
                    className="font-aref font-bold line-clamp-2"
                    style={{ color: PRIMARY, fontSize: "clamp(1rem, 2vw, 1.25rem)", lineHeight: 1.4, cursor: "pointer" }}
                    onClick={() => go(event.slug)}
                  >
                    {event.title}
                  </h3>

                  <p
                    className="font-abel text-sm line-clamp-2"
                    style={{ color: "#888", lineHeight: 1.65 }}
                    dangerouslySetInnerHTML={{ __html: event.body }}
                  />

                  <div className="flex items-center gap-1.5 mt-1" onClick={() => go(event.slug)}>
                    <span className="font-abel text-xs uppercase tracking-widest font-bold" style={{ color: GOLD }}>
                      View Event
                    </span>
                    <ArrowForward style={{ fontSize: 12, color: GOLD }} />
                  </div>
                </div>

                {/* Decorative cross panel */}
                <div
                  className="hidden sm:flex items-center justify-center shrink-0"
                  style={{
                    width: 76,
                    borderLeft: `1px solid ${GOLD}33`,
                    background: `linear-gradient(160deg, ${GOLD}08 0%, #fff 100%)`,
                  }}
                >
                  <svg viewBox="0 0 80 80" width={52} height={52} fill="none">
                    <circle cx="40" cy="40" r="36" stroke={PRIMARY} strokeWidth="1" strokeDasharray="4 4" opacity="0.3" />
                    <circle cx="40" cy="40" r="28" stroke={GOLD} strokeWidth="0.8" opacity="0.5" />
                    <rect x="35" y="16" width="10" height="48" rx="2" fill={GOLD} stroke={PRIMARY} strokeWidth="1.2" />
                    <rect x="16" y="30" width="48" height="10" rx="2" fill={GOLD} stroke={PRIMARY} strokeWidth="1.2" />
                    <circle cx="40" cy="40" r="4" fill={PRIMARY} />
                    <circle cx="40" cy="40" r="2" fill={GOLD} />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mx-auto">
        <Pagination
          totalPosts={events.length}
          postsPerPage={postsPerPage}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
        />
      </div>
    </Fragment>
  );
};
